import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Tooltip, OverlayTrigger, Button } from "react-bootstrap";
import DateTime from "../../../../components/Formatters/DateTime";
import TextSpinner from "../../../../components/UI/Spinner/TextSpinner";
import useDetalhamento from "../hooks/useDetalhamento";
import FormDemonstrativos from "../FormDemonstrativos";
import useMontarLogica from "../hooks/useMontarLogica";
import useBuscaComposicao from "../hooks/useBuscaComposicao";
import useExcluirDemonstrativo from "../hooks/useExcluirDemonstrativo";
import constants from "../../../Constants";

const getColumns = (mostrarSentidoOperacao) =>
  [
    {
      name: <div>Nº Nota Fiscal</div>,
      selector: (row) => row.nuItem,
      minWidth: "7%",
    },
    {
      name: "Chave",
      selector: (row) => row.txChave,
      minWidth: "10%",
    },
    {
      name: <div>Data Emissão</div>,
      selector: (row) => row.dtEmissao,
      minWidth: "7%",
      cell: (row) => <DateTime value={row.dtVencimento} stringFormat="dd/MM/yyyy" />,
      center: true,
    },
    {
      name: <div>Data Vencimento</div>,
      selector: (row) => row.dtVencimento,
      minWidth: "7%",
      cell: (row) => <DateTime value={row.dtVencimento} stringFormat="dd/MM/yyyy" />,
      center: true,
    },
    {
      name: mostrarSentidoOperacao === "S" ? <div>CNPJ/CPF Destinatário</div> : <div>CNPJ/CPF Origem</div>,
      selector: (row) => row.nuCnpjRemet,
      minWidth: "10%",
      center: true,
    },
    {
      name: mostrarSentidoOperacao === "S" ? <div>UF Destino</div> : <div>UF Origem</div>,
      selector: (row) => row.siglaUf,
      minWidth: "5%",
      center: true,
    },
    {
      name: <div>Descrição</div>,
      selector: (row) => <div dangerouslySetInnerHTML={{ __html: row.dsItem }} />,
      minWidth: "12%",
    },
    {
      name: <div>Valor Produto (R$)</div>,
      selector: (row) =>
        row.vlProduto
          ? row.vlProduto.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
      center: true,
    },
    {
      name: <div>Base Cálculo (R$)</div>,
      selector: (row) =>
        row.vlBaseCalculo
          ? row.vlBaseCalculo.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
      center: true,
    },
    {
      name: <div>ICMS NFE (R$)</div>,
      selector: (row) =>
        row.vlIcmsRecolhido
          ? row.vlIcmsRecolhido.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
      center: true,
    },
    {
      name: <div>ICMS Correto (R$)</div>,
      selector: (row) =>
        row.vlIcmsCalculado
          ? row.vlIcmsCalculado.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
      center: true,
    },
    {
      name: <div>ICMS a Recolher (R$)</div>,
      selector: (row) =>
        row.vlIcms
          ? row.vlIcms.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
      center: true,
    },
    {
      name: <div>Multa (R$)</div>,
      selector: (row) =>
        row.vlMultaOcorrenciaAtual
          ? row.vlMultaOcorrenciaAtual.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
      center: true,
    },
    {
      name: <div>[ICMS + Multa] (R$) </div>,
      selector: (row) =>
        (row.vlMultaOcorrenciaAtual + row.vlIcms).toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),

      minWidth: "5%",
      center: true,
    },
  ].filter(Boolean);

const conditionalRowStyles = [
  {
    when: (row) => row.nuItem === "Total",
    style: {
      backgroundColor: "#f0f0f0",
      cursor: "not-allowed",
      fontWeight: "bold", // Deixa a fonte em negrito
      color: "#333", // (opcional) ajusta a cor da fonte pra ficar mais visível
    },
  },
];

const ListagemAI = ({ history, sqItemOcorrencia, sqAcaoFiscal }) => {
  const [reload, setReload] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const { totalRows, dataWithTotal, isLoading, handlePageChange } = useDetalhamento({
    sqAcaoFiscal,
    sqItemOcorrencia,
    reload, // Passando para o hook para atualizar os dados
  });

  const [mostrarSentidoOperacao, setMostrarSentidoOperacao] = useState(true);

  const { buscaComposicao, sentidoOperacao } = useBuscaComposicao({ sqAcaoFiscal, sqItemOcorrencia });

  useEffect(() => {
    if (sqAcaoFiscal !== null) {
      buscaComposicao();
    }
  }, [sqAcaoFiscal, sqItemOcorrencia, buscaComposicao]);

  useEffect(() => {
    if (sentidoOperacao) {
      setMostrarSentidoOperacao(sentidoOperacao);
    }
  }, [sentidoOperacao, setMostrarSentidoOperacao]);

  const [dados, setDados] = useState({});

  const { montarLogica, mostrarLista, setMostrarLista } = useMontarLogica();

  useEffect(() => {
    if (mostrarLista) {
      montarLogica(1);
    }
  }, [montarLogica, mostrarLista, reload]);

  const voltarListagem = () => {
    setMostrarLista(true);
    setReload((prev) => prev + 1);
  };

  const { ModalExcluirDemonstrativo, setShowExcluirConfirm } = useExcluirDemonstrativo({
    setReload,
    setItensSelecionados,
  });

  const columns = useMemo(() => getColumns(mostrarSentidoOperacao), [mostrarSentidoOperacao]);

  const preparaDelete = useCallback(() => {
    if (itensSelecionados.length > 0) {
      setShowExcluirConfirm({
        show: true,
        body: `Confirma exclusão do(s) item(ns) no demonstrativo?`,
        context: { sqAcaoFiscal, sqItemOcorrencia, listaItens: itensSelecionados },
      });

      // Resetando a seleção e forçando novo fetch dos dados
      setClearSelectedRows(!clearSelectedRows);
      setItensSelecionados([]);

      // Força o reload completo para pegar os dados atualizados
      setReload((prev) => !prev);
    } else {
      document.getElementById("alerta").innerHTML = "Selecione pelo menos 1 item para excluir";
    }
  }, [itensSelecionados, sqAcaoFiscal, sqItemOcorrencia, setShowExcluirConfirm, clearSelectedRows]);

  return mostrarLista ? (
    <>
      {ModalExcluirDemonstrativo}
      <DataTable
        noHeader
        keyField={(row, index) => `${row.sqItemOcorrencia}-${index}`}
        columns={columns}
        data={dataWithTotal}
        progressPending={isLoading}
        noDataComponent="Nenhum registro para exibir"
        progressComponent={<TextSpinner />}
        pointerOnHover
        onRowClicked={(row) => {
          if (row.nuItem !== "Total") {
            setDados(row);
            montarLogica(4);
          }
        }}
        conditionalRowStyles={conditionalRowStyles}
        pagination
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        paginationComponentOptions={constants.paginationComponentOptions}
        paginationPerPage={10}
        selectableRows
        selectableRowDisabled={(row) => row.nuItem === "Total"}
        selectableRowsVisibleOnly={(row) => !(row.nuItem === "Total")}
        onSelectedRowsChange={(state) => {
          setItensSelecionados(state.selectedRows);
        }}
        clearSelectedRows={clearSelectedRows}
      />
      <span className="alerta" hidden={itensSelecionados.length > 0} id="alerta">
        &nbsp;
      </span>

      <br />
      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-chave">Excluir itens do demonstrativo</Tooltip>}>
        <Button
          className="btn btn-primary btn-md mt-1" // btn btn-primary btn-md mr-2 btn btn-primary
          style={{ marginTop: "3px" }} // CSS inline direto
          disabled={itensSelecionados.length === 0}
          onClick={() => preparaDelete()}
        >
          Excluir
        </Button>
      </OverlayTrigger>
    </>
  ) : (
    <FormDemonstrativos
      tipoDetalhamento={dados.tipoDetalhamento}
      sqItemOcorrencia={dados.sqItemOcorrencia}
      sqAcaoFiscal={dados.sqAcaoFiscal}
      dadosOcorrencia={dados}
      history={history}
      voltarListagem={() => voltarListagem()}
    />
  );
};

export default ListagemAI;
